/**
 * Listado de centros.
 *
 * La columna que nadie más tiene: **si el centro está sujeto a inspección
 * periódica** por un organismo de control (art. 22 RIPCI). Es una decisión
 * del motor normativo materializada en la base, no una casilla que alguien
 * marcó a mano.
 */

import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../table/data-table.js';
import { useListing } from '../table/use-listing.js';

interface SiteRow {
  id: string;
  code: string;
  name: string;
  customer_name: string;
  main_use: string;
  built_area_m2: string | null;
  has_high_risk_area: boolean;
  is_industrial_establishment: boolean;
  inspection_required: boolean | null;
  inspection_interval_years: number | null;
  city: string | null;
  equipment_count: string;
  overdue_count: string;
}

const USE_LABEL: Record<string, string> = {
  RESIDENCIAL_VIVIENDA: 'Residencial',
  ADMINISTRATIVO: 'Administrativo',
  DOCENTE: 'Docente',
  COMERCIAL: 'Comercial',
  PUBLICA_CONCURRENCIA: 'Pública concurrencia',
  APARCAMIENTO: 'Aparcamiento',
  INDUSTRIAL: 'Industrial',
  HOSPITALARIO: 'Hospitalario',
  OTROS: 'Otros',
};

const COLUMNS: Column<SiteRow>[] = [
  { id: 'code', label: 'Código', render: (r) => r.code, sortable: true, width: '7rem' },
  { id: 'name', label: 'Centro', render: (r) => r.name, sortable: true },
  { id: 'customerName', label: 'Cliente', render: (r) => r.customer_name },
  { id: 'city', label: 'Población', render: (r) => r.city ?? '—' },
  {
    id: 'mainUse',
    label: 'Uso principal',
    sortable: true,
    render: (r) => USE_LABEL[r.main_use] ?? r.main_use,
    exportValue: (r) => USE_LABEL[r.main_use] ?? r.main_use,
  },
  {
    id: 'inspectionRequired',
    label: 'Inspección periódica',
    width: '12rem',
    render: (r) => {
      // `null` no es «no»: es «el motor todavía no lo ha decidido», casi
      // siempre porque falta un dato de cumplimiento del centro.
      if (r.inspection_required === null) {
        return <span className="pill pill--warn">? Sin determinar</span>;
      }
      return r.inspection_required ? (
        <span className="pill pill--info">
          ✓ Cada {r.inspection_interval_years ?? '—'} años
        </span>
      ) : (
        <span className="pill pill--off">• No sujeto</span>
      );
    },
    exportValue: (r) =>
      r.inspection_required === null
        ? 'Sin determinar'
        : r.inspection_required
          ? `Cada ${r.inspection_interval_years ?? '?'} años`
          : 'No sujeto',
  },
  {
    id: 'equipmentCount',
    label: 'Equipos',
    numeric: true,
    width: '6rem',
    render: (r) => r.equipment_count,
  },
  {
    id: 'overdueCount',
    label: 'Vencidos',
    numeric: true,
    width: '6rem',
    render: (r) =>
      Number(r.overdue_count) > 0 ? (
        <span className="due due--late">⚠ {r.overdue_count}</span>
      ) : (
        <span className="due--none">0</span>
      ),
    exportValue: (r) => r.overdue_count,
  },
  {
    id: 'builtArea',
    label: 'Superficie m²',
    numeric: true,
    render: (r) => r.built_area_m2 ?? '—',
  },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

const QUICK = [
  { id: 'all', label: 'Todos', param: {} },
  { id: 'inspection', label: 'Con inspección periódica', param: { inspectionRequired: 'true' } },
  { id: 'risk', label: 'Con riesgo especial', param: { highRisk: 'true' } },
] as const;

export function SitesScreen(): React.ReactNode {
  // Del router, no de `window.location`: leerlo del navegador no vuelve a
  // renderizar y el filtro rápido se quedaría pintado sin filtrar nada.
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const quick = QUICK.find((q) => q.id === params.get('v')) ?? QUICK[0];

  const listing = useListing<SiteRow>({
    path: '/sites',
    columnIds: COLUMN_IDS,
    extraParams: quick.param,
    preserve: ['v'],
  });

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Centros</h1>
        <div className="chips" role="group" aria-label="Filtros rápidos">
          {QUICK.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chip${quick.id === option.id ? ' chip--on' : ''}`}
              aria-pressed={quick.id === option.id}
              onClick={() => listing.setParam('v', option.id === 'all' ? null : option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <Link className="button button--primary button--small" to="/centros/nuevo">
          Nuevo centro
        </Link>
        {listing.total !== null && <span className="screen__count">{listing.total} centros</span>}
      </header>

      {listing.error !== null && (
        <p className="screen__error" role="alert">
          {listing.error}
        </p>
      )}

      <DataTable
        columns={COLUMNS}
        rows={listing.rows}
        idOf={(r) => r.id}
        state={listing.state}
        onStateChange={listing.setState}
        exportName="centros"
        loading={listing.loading}
        hasMore={listing.hasMore}
        onLoadMore={listing.loadMore}
        totalMatching={listing.total}
        onOpenRow={(row) => void navigate(`/centros/${row.id}`)}
        emptyMessage="No hay centros que coincidan."
      />
    </section>
  );
}
